import React, { useContext, useEffect, useState } from "react";
import { Card, Table, Tag, Typography, Space, Tooltip, Button, ModalFuncProps, Modal, FloatButton } from "antd";
import { ColumnsType } from "antd/es/table";
import { Content } from "antd/es/layout/layout";
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MenuOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import AppSider from "../../components/AppSider";
import { BiArrowFromLeft, BiEdit } from "react-icons/bi";
import { SyncOutlined, PlusOutlined, UserOutlined } from "@ant-design/icons";
import CazaModal from "./CazaModal";
import EditAttemptsModal from "./EditAttemptsModal";
import NouCazaModal from "./NouCazaModal";
import CazaAdminModal from "./CazaAdminModal";
import CopyPildoraModal from "./CopyPildoraModal";
import ImportarCazaExcelModal from "./ImportarCazaExcelModal";
import JutgeColors from "../../../../styles/JutgeColors";
import { FaCopy } from "react-icons/fa";
import { GetJutgeCaza } from "../../../../dao/jutge/GetJutgeCaza";
import { AuthContext } from "../../../../config/AuthContext";
import UseWebSocketSubscription from "../../../UseWebSocketSubscription";
import { PostCazaStart } from "../../../../dao/jutge/PostCazaStart";
import { ClaimCazaReward } from "../../../../dao/jutge/ClaimCazaReward";
import { CheckCircleOutlined, GiftOutlined, CloseCircleOutlined, FileExcelOutlined, TrophyOutlined, LockOutlined } from "@ant-design/icons";
import { EditOutlined, LoginOutlined } from "@mui/icons-material";
import JavaMon from "../../problemesId/content/JavaMon";
import { EnsureCazaJavamon } from "../../../../dao/jutge/EnsureCazaJavamon";
import { getJavamonImage as getExternalJavamonImage } from "../../../../dao/jutge/GetJavamonConfig";
import RecompensaSummaryModal from "../../activitatsId/content/RecompensaSummaryModal";
import { message } from "antd";
import PildoraRecompensesModal from "../../pildoresId/content/PildoraRecompensesModal";
import { CazaRecompensesTags } from "../../pildoresId/content/CazaRecompensesTags";
import { GetCazaRecompenses } from "../../../../dao/jutge/CazaRecompensesDao";

function normalizeImagePath(path: string): string {
    if (!path) return "";
    let clean = path;
    if (clean.startsWith("public/")) {
        clean = clean.replace("public/", "/");
    } else if (clean.startsWith("public")) {
        clean = clean.replace("public", "");
    }
    if (!clean.startsWith("http") && !clean.startsWith("/")) {
        clean = "/" + clean;
    }
    return clean;
}

const { Text } = Typography;

interface EscenarioCaza {
    id: number;
    nombre: string;
    descripcion: string;
    dificultad: "Beginner" | "Intermediate" | "Expert";
    imagen: string;
    background?: string;
    nivel?: number;
    rarity?: "normal" | "epic" | "legendary";
    instrucciones: string;

    vidaJavamon: number;
    mustInclude: string[];
    mustNotInclude: string[];
    intentos_restantes?: number;
    intentos_global?: number;
    estat?: number; // -1: Pending, 0: Failed, 1: Success
    obtained?: number; // 0: Not claimed, 1: Claimed
    estat_config?: number;
    javamon_config_id?: number;
    url_teoria?: string;
    totalPasos?: number;
    pasos?: any[];
    id_aula?: number;
    aula?: string;
    max_paso_completado?: number; // highest step with estat=1
    rewardType?: string;
    reward_type?: string;
    tipo_recompensa?: string;
    itemId?: number;
    id_item?: number;
    reward_valor?: number;
    reward_limit_quantity?: number;
    reward_intentos_min?: number;
    reward_intentos_max?: number;
    reward_time_limit?: number;
    tiempo_empleado_segundos?: number;
    tiempo_limite_superado?: number;
    id_origen?: number;
    linked_aules_count?: number;
    ordre?: number;
    id_bloqueo?: number;
    bloqueo_nombre?: string;
    bloqueo_intentado?: number;
}


const dificultadColor: Record<string, string> = {
    Beginner: "green",
    Intermediate: "orange",
    Expert: "red",
};

// Helper to get random image based on ID
const getJavamonImage = (id: number) => {
    const num = (id % 5) + 1; // 1-5
    return `/images/javamon/t1/normal/JM00${num}.png`;
};

const CazaImagen: React.FC<{ id: number }> = ({ id }) => {
    const [src, setSrc] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        // Try fetch DB image
        fetch(`${process.env.REACT_APP_API_URL}:8061/jutge/caza/${id}/imagen`)
            .then(res => {
                if (res.ok) return res.json();
                return null;
            })
            .then(data => {
                if (mounted && data && data.background) {
                    setSrc(data.background); // data:image... from backend
                }
            })
            .catch(() => {});
        return () => { mounted = false; };
    }, [id]);

    return (
        <img
            src={src || ""}
            style={{
                width: 50,
                height: 50,
                objectFit: "cover",
                borderRadius: 12,
                boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
            }}
        />
    );
};

const Row = (props: any) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props['data-row-key'],
    });

    const style: React.CSSProperties = {
        ...props.style,
        transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
        transition,
        cursor: 'move',
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };

    return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};

const Caza = () => {
    const [escenarioActivo, setEscenarioActivo] = useState<EscenarioCaza | null>(null);
    const [escenarios, setEscenarios] = useState<EscenarioCaza[]>([]);
    const { isLoggedIn, userId, user, setBreadCrumb } = useContext(AuthContext);
    const [editAttemptsVisible, setEditAttemptsVisible] = useState(false);
    const [newCazaVisible, setNewCazaVisible] = useState(false);
    const [editingCaza, setEditingCaza] = useState<EscenarioCaza | null>(null); // State for editing Caza fields
    const [selectedCaza, setSelectedCaza] = useState<EscenarioCaza | null>(null);
    const [loading, setLoading] = useState(false);
    const [selectedAulaFilter, setSelectedAulaFilter] = useState<number | string | null>('all');

    const isAdmin = user && (user.tipus === "0" || user.tipus === "1");

    const handleEstadoChange = (id: number, nuevoEstado: number) => {
        fetch(`${process.env.REACT_APP_API_URL}:8061/jutge/caza/${id}/estat`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ estat_config: nuevoEstado })
        }).then(res => {
            if(res.ok) {
                message.success("Estado actualizado");
                fetchData();
            } else {
                message.error("Error al actualizar el estado");
            }
        }).catch(err => {
            message.error("Error de red");
        });
    };

    // Admin Stats Modal
    const [adminModalVisible, setAdminModalVisible] = useState(false);
    const [activeAdminCaza, setActiveAdminCaza] = useState<EscenarioCaza | null>(null);

    // Copy Pildora Modal
    const [modalCopyVisible, setModalCopyVisible] = useState(false);
    const [copyCaza, setCopyCaza] = useState<EscenarioCaza | null>(null);

    // Import Excel Modal
    const [importExcelVisible, setImportExcelVisible] = useState(false);

    // Battle State
    const [showBattle, setShowBattle] = useState(false);
    const [battleJavamon, setBattleJavamon] = useState<{ name: string, image: string, rarity: "normal" | "epic" | "legendary" } | null>(null);
    const [activeBattleCazaId, setActiveBattleCazaId] = useState<number | null>(null);

    // Reward Modal State (Unified with ActivitatsIdExercicis)
    const [recompensaModalVisible, setRecompensaModalVisible] = useState(false);
    const [recompensaItems, setRecompensaItems] = useState<any[]>([]);
    const [selectableMode, setSelectableMode] = useState(false);
    const [selectionOptions, setSelectionOptions] = useState<any[]>([]);
    const [pendingSelectionItem, setPendingSelectionItem] = useState<EscenarioCaza | null>(null);
    const [pendingJavamonBattle, setPendingJavamonBattle] = useState<{ idCaza: number, javamon: any } | null>(null);
    const [cazaRecompensesVisible, setCazaRecompensesVisible] = useState(false);
    const [selectedCazaRecompenses, setSelectedCazaRecompenses] = useState<EscenarioCaza | null>(null);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [cazaBloqueoModalVisible, setCazaBloqueoModalVisible] = useState(false);
    const [selectedCazaBloqueo, setSelectedCazaBloqueo] = useState<EscenarioCaza | null>(null);
    const [selectedCazaBloqueoId, setSelectedCazaBloqueoId] = useState<number | undefined>(undefined);
    const [savingCazaBloqueo, setSavingCazaBloqueo] = useState(false);

    const handleOpenBloqueoModal = (caza: EscenarioCaza, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedCazaBloqueo(caza);
        setSelectedCazaBloqueoId(caza.id_bloqueo || undefined);
        setCazaBloqueoModalVisible(true);
    };

    const handleSaveCazaBloqueo = async () => {
        if (!selectedCazaBloqueo) return;
        setSavingCazaBloqueo(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}:8061/jutge/caza/${selectedCazaBloqueo.id}/bloqueo`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_bloqueo: selectedCazaBloqueoId || null })
            });
            if (res.ok) {
                message.success("Bloqueo de acceso actualizado correctamente");
                setCazaBloqueoModalVisible(false);
                setSelectedCazaBloqueo(null);
                fetchData();
            } else {
                message.error("Error al actualizar el bloqueo de acceso");
            }
        } catch (err) {
            console.error("Error guardando bloqueo de caza:", err);
            message.error("Error al actualizar el bloqueo de acceso");
        } finally {
            setSavingCazaBloqueo(false);
        }
    };

    const handleEditAttempts = (caza: EscenarioCaza, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedCaza(caza);
        setEditAttemptsVisible(true);
    };

    const handleEditCaza = (caza: EscenarioCaza, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingCaza(caza);
        setNewCazaVisible(true);
    };

    const fetchData = () => {
        if (userId) {
            setLoading(true);
            GetJutgeCaza(userId).then((data: any[]) => {
                if (data) {
                    const mappedEscenarios: EscenarioCaza[] = data.map(item => ({
                        ...item,
                        mustInclude: item.mustInclude
                            ? item.mustInclude.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0)
                            : [],
                        mustNotInclude: item.mustNotInclude
                            ? item.mustNotInclude.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0)
                            : []
                    }));
                    setEscenarios(mappedEscenarios);
                }
                setLoading(false);
            });
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 1,
            },
        })
    );

    const onDragEnd = ({ active, over }: any) => {
        if (active.id !== over?.id) {
            setEscenarios((previous) => {
                const currentDisplayed = selectedAulaFilter && selectedAulaFilter !== 'all' 
                    ? previous.filter(e => e.id_aula === selectedAulaFilter) 
                    : [...previous];

                const activeIndex = currentDisplayed.findIndex((i) => i.id === active.id);
                const overIndex = currentDisplayed.findIndex((i) => i.id === over?.id);
                if (activeIndex === -1 || overIndex === -1) return previous;

                const movedDisplayed = arrayMove(currentDisplayed, activeIndex, overIndex);
                
                // Update order in backend for the moved list
                const ordenList = movedDisplayed.map((item, index) => ({ id: item.id, ordre: index + 1 }));
                fetch(`${process.env.REACT_APP_API_URL}:8061/jutge/caza/ordre`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(ordenList)
                }).catch(() => message.error("Error guardando el orden"));

                if (selectedAulaFilter && selectedAulaFilter !== 'all') {
                    // Merge back into previous retaining non-displayed items
                    return previous.map(item => {
                        const foundInMoved = movedDisplayed.find(m => m.id === item.id);
                        return foundInMoved ? { ...item, ordre: movedDisplayed.indexOf(foundInMoved) + 1 } : item;
                    }).sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
                }

                return movedDisplayed;
            });
        }
    };

    const handleClaimAndRefresh = async (record: EscenarioCaza) => {
        if (record.tiempo_limite_superado === 1) {
            message.error(`Has superado el tiempo límite de ${record.reward_time_limit} minutos para conseguir la recompensa.`);
            return;
        }
        let allRecs: any[] = [];
        try {
            message.loading({ content: "Revisando recompensas...", key: "claim_caza" });
            allRecs = (await GetCazaRecompenses(record.id)) || [];
            const groupedRewards = allRecs.filter((r: any) => Number(r.grupo_seleccion) > 0);

            if (groupedRewards.length > 0) {
                message.destroy("claim_caza");
                setSelectionOptions(groupedRewards);
                setPendingSelectionItem(record);
                setSelectableMode(true);
                setRecompensaModalVisible(true);
                return;
            }
        } catch (_) {}

        // Determinar si hay recompensa de tipo Javamon
        let isJavamonReward = false;
        if (allRecs.length > 0) {
            isJavamonReward = allRecs.some((r: any) => {
                const t = (r.tipo || '').toUpperCase();
                return t === 'JAVAMON' || t === 'JAVAMON_RANDOM' || t === 'JAVAMON_SEASON';
            });
        } else {
            const rawType = record.reward_type || record.rewardType;
            if (rawType) {
                const rt = rawType.toUpperCase();
                isJavamonReward = rt === 'JAVAMON' || rt === 'JAVAMON_RANDOM' || rt === 'JAVAMON_SEASON';
            } else {
                isJavamonReward = Boolean(record.javamon_config_id);
            }
        }

        if (isJavamonReward) {
            try {
                message.loading({ content: "Detectando recompensa...", key: "claim_caza" });
                const res = await EnsureCazaJavamon(record.id + "", userId);
                message.destroy("claim_caza");

                if (res && (res.image || res.nom || res.id)) {
                    let r: "normal" | "epic" | "legendary" = "normal";
                    if (res.image && res.image.includes("epic")) r = "epic";
                    else if (res.image && res.image.includes("legendary")) r = "legendary";

                    const jm = {
                        id: res.id,
                        name: res.nom || "Javamon",
                        image: getExternalJavamonImage(res),
                        rarity: r
                    };

                    setPendingJavamonBattle({
                        idCaza: record.id,
                        javamon: jm
                    });
                    setSelectableMode(false);
                    setRecompensaItems([{ tipo: 'JAVAMON', data: jm }]);
                    setRecompensaModalVisible(true);
                } else {
                    message.error({ content: "No se ha encontrado Javamon asignado.", key: "claim_caza" });
                }
            } catch (err) {
                message.error({ content: "Error al preparar la recompensa", key: "claim_caza" });
            }
        } else {
            // Direct claim for PUNTUACION or ITEMS/SKINS/EQUIPMENT
            try {
                message.loading({ content: "Reclamando recompensa...", key: "claim_caza" });
                const res = await ClaimCazaReward(record.id, userId);
                if (res && res.status === 'ok') {
                    message.success({ content: res.message || "¡Recompensa obtenida!", key: "claim_caza" });
                    setSelectableMode(false);
                    setPendingJavamonBattle(null);
                    setRecompensaItems(res.claimed && res.claimed.length > 0 ? res.claimed : (
                        allRecs.length > 0 ? allRecs : [
                            { tipo: 'PUNTUACION', valor: record.reward_valor || 5 }
                        ]
                    ));
                    setRecompensaModalVisible(true);
                    fetchData();
                } else {
                    message.error({ content: (res && res.message) || "Error al reclamar la recompensa", key: "claim_caza" });
                }
            } catch (e) {
                message.error({ content: "Error de red al reclamar", key: "claim_caza" });
            }
        }
    };

    const handleConfirmSelection = async (chosenReward: any) => {
        if (!pendingSelectionItem) return;
        try {
            message.loading({ content: "Reclamando recompensa seleccionada...", key: "claim_caza" });
            const res = await ClaimCazaReward(pendingSelectionItem.id, userId, null, chosenReward.id);
            if (res && res.status === 'ok') {
                message.success({ content: res.message || "¡Recompensa elegida con éxito!", key: "claim_caza" });
                setSelectableMode(false);
                setRecompensaItems(res.claimed && res.claimed.length > 0 ? res.claimed : [chosenReward]);
                fetchData();
            } else {
                message.error({ content: (res && res.message) || "Error al reclamar la selección", key: "claim_caza" });
            }
        } catch (e) {
            message.error({ content: "Error de red al reclamar selección", key: "claim_caza" });
        }
    };

    useEffect(() => {
        fetchData();
    }, [userId]);

    UseWebSocketSubscription(
        [`/topic/caza/${userId}`],
        async (topic: any, msg: any) => {
            if (msg === true || msg.accion === "REFRESH") {
                fetchData();
            }
        }
    );

    const aulaFilters = Array.from(
        new Map((Array.isArray(escenarios) ? escenarios : [])
            .filter(e => e.id_aula !== undefined && e.id_aula !== null)
            .map(e => [e.id_aula!, { text: e.aula || `Aula ${e.id_aula}`, value: e.id_aula! }])
        ).values()
    );

    // For antd Select (needs {label, value})
    const aulaSelectOptions = [
        { label: "Todas las aulas", value: "all" },
        ...aulaFilters.map(f => ({ label: (f as any).text, value: f.value }))
    ];

    const baseColumns: ColumnsType<EscenarioCaza> = [

        {
            key: "id",
            dataIndex: "id",
            width: 20
        },
        {
            key: "state",
            title: "Estado",
            align: "center",
            width: 140,
            render: (_, record) => {
                const { estat, obtained, estat_config, totalPasos, max_paso_completado } = record;
                const totalP = totalPasos || 0;
                const maxCompletado = max_paso_completado || 0;
                const allStepsDone = totalP > 0 && maxCompletado >= totalP;

                // ── Colored tag based on config + progress ──
                const getConfigTag = () => {
                    if (estat_config === 2) return <Tag color="#8c8c8c"  style={{ fontWeight: 600, margin: 0 }}>Finalitzat</Tag>;
                    if (estat_config === 0) return <Tag color="#fa8c16"  style={{ fontWeight: 600, margin: 0 }}>En Edició</Tag>;
                    // estat_config === 1 → Visible
                    if (obtained === 1)     return <Tag color="#52c41a"  style={{ fontWeight: 600, margin: 0 }} icon={<CheckCircleOutlined />}>Realizado</Tag>;
                    if (estat === -1)        return <Tag color="#1890ff"  style={{ fontWeight: 600, margin: 0 }} icon={<SyncOutlined spin />}>Evaluando...</Tag>;
                    if (allStepsDone && (!obtained || obtained === 0))
                                            return <Tag color="#faad14"  style={{ fontWeight: 700, margin: 0, color: '#7d4e00' }} icon={<GiftOutlined />}>¡Recompensa!</Tag>;
                    if (totalP > 0 && maxCompletado > 0)
                                            return <Tag color="#13c2c2"  style={{ fontWeight: 600, margin: 0 }} icon={<SyncOutlined />}>{maxCompletado} de {totalP}</Tag>;
                    if (estat === 0)         return <Tag color="#f5222d"  style={{ fontWeight: 600, margin: 0 }} icon={<CloseCircleOutlined />}>Fallado</Tag>;
                    return                         <Tag color="#d9d9d9"  style={{ fontWeight: 600, margin: 0, color: '#595959' }}>Pendiente</Tag>;
                };

                if (isAdmin) {
                    return (
                        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                            {getConfigTag()}
                            <Select
                                value={estat_config === 2 ? 2 : (estat_config === 0 ? 0 : 1)}
                                onChange={(val) => handleEstadoChange(record.id, val)}
                                style={{ width: '110px' }}
                                size="small"
                                options={[
                                    { value: 0, label: <><span style={{ color: '#fa8c16', fontWeight: 700 }}>● </span>En Edició</> },
                                    { value: 1, label: <><span style={{ color: '#52c41a', fontWeight: 700 }}>● </span>Visible</> },
                                    { value: 2, label: <><span style={{ color: '#8c8c8c', fontWeight: 700 }}>● </span>Finalitzat</> },
                                ]}
                            />
                        </div>
                    );
                }

                return getConfigTag();
            }
        },

        {
            key: "imagen",
            dataIndex: "imagen",
            width: 60,
            render: (_: any, record: EscenarioCaza) => <CazaImagen id={record.id} />,
        },
        {
            key: "nombre",
            dataIndex: "nombre",
            title: "Píldora",
            filters: aulaFilters.length > 0 ? aulaFilters : undefined,
            onFilter: (value, record) => record.id_aula === value,
            render: (text, record) => {
                const colors = [
                    JutgeColors.colors.color1,
                    JutgeColors.colors.color2,
                    JutgeColors.colors.color3,
                    JutgeColors.colors.color4,
                    JutgeColors.colors.color5,
                    JutgeColors.colors.color6
                ];
                const aulaColor = record.id_aula ? colors[record.id_aula % colors.length] : "default";

                return (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <Text strong style={{ fontSize: "1rem" }}>
                            {text}
                        </Text>
                        <Text type="secondary" style={{ fontSize: "0.8rem", marginBottom: 2 }}>
                            {record.descripcion}
                        </Text>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            {record.id_aula ? (
                                <Tag color={aulaColor} style={{ fontSize: "0.75rem", marginTop: 2, marginRight: 0 }}>
                                    {record.aula || `Aula ${record.id_aula}`}
                                </Tag>
                            ) : (
                                <Tag color="default" style={{ fontSize: "0.75rem", marginTop: 2, marginRight: 0 }}>
                                    Global
                                </Tag>
                            )}
                            {isAdmin && record.linked_aules_count && record.linked_aules_count > 1 ? (
                                <Tooltip title={`Píldora común sincronizada en ${record.linked_aules_count} aulas`}>
                                    <Tag color="purple" style={{ fontSize: "0.75rem", marginTop: 2, marginRight: 0 }}>
                                        🔗 {record.linked_aules_count} aulas
                                    </Tag>
                                </Tooltip>
                            ) : null}
                        </div>
                        <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                            <CazaRecompensesTags
                                id_caza={record.id}
                                ele={record}
                                refreshKey={refreshKey}
                            />
                        </div>
                    </div>
                );
            },
        },
        {
            key: "dificultad",
            dataIndex: "dificultad",
            title: "Dificultad",
            align: "center",
            render: (d: string) => (
                <Tag
                    color={dificultadColor[d]}
                    style={{ fontWeight: "bold" }}
                >
                    {d}
                </Tag>
            ),
        },
        {
            key: "intentos",
            title: "Intentos",
            align: "center",
            render: (_, record) => {
                const isUnlimited = record.intentos_global === 0 || record.intentos_global === null || record.intentos_global === undefined;
                if (isUnlimited) {
                    return <Tag color="green">Ilimitados</Tag>;
                }
                const restantes = record.intentos_restantes !== undefined ? record.intentos_restantes : 0;
                const total = record.intentos_global;
                return (
                    <Tag color={restantes > 0 ? "blue" : "red"}>
                        {restantes} / {total}
                    </Tag>
                );
            }
        },
        {
            key: "tiempo",
            title: "Tiempo",
            align: "center",
            render: (_, record) => {
                const limitMin = record.reward_time_limit;
                const elapsedSec = record.tiempo_empleado_segundos;
                const isExceeded = record.tiempo_limite_superado === 1;

                const formatSec = (sec?: number) => {
                    if (sec === undefined || sec === null) return "--:--";
                    const m = Math.floor(sec / 60);
                    const s = sec % 60;
                    return `${m}m ${s < 10 ? '0' : ''}${s}s`;
                };

                return (
                    <Space direction="vertical" size={2}>
                        {limitMin ? (
                            <Tooltip title={`Tiempo límite para recompensa: ${limitMin} minutos`}>
                                <Tag color={isExceeded ? "red" : "orange"} style={{ fontSize: "0.75rem" }}>
                                    ⏱️ Máx: {limitMin}m
                                </Tag>
                            </Tooltip>
                        ) : null}
                        {elapsedSec !== undefined && elapsedSec !== null ? (
                            <Tooltip title={`Tiempo empleado: ${formatSec(elapsedSec)}`}>
                                <Tag color={isExceeded ? "volcano" : "cyan"} style={{ fontSize: "0.75rem" }}>
                                    {isExceeded ? "⚠️ " : "⌛ "} {formatSec(elapsedSec)}
                                </Tag>
                            </Tooltip>
                        ) : (
                            !limitMin && <span style={{ color: "#aaa", fontSize: "0.8rem" }}>-</span>
                        )}
                    </Space>
                );
            }
        },
        {
            key: "acciones",
            title: "Acciones",
            align: "center",
            render: (_, record) => {
                const { estat, obtained, estat_config } = record;
                const isFinalized = estat_config === 2;
                const isFailed = estat === 0;
                const isCaptured = obtained === 1;
                const isEvaluating = estat === -1;
                const isUnlimited = record.intentos_global === 0 || record.intentos_global === null || record.intentos_global === undefined;
                const isOutOfAttempts = !isUnlimited && record.intentos_restantes !== undefined && record.intentos_restantes <= 0;
                const totalP = record.totalPasos || 0;
                const maxCompletado = record.max_paso_completado || 0;
                const allStepsDone = totalP > 0 && maxCompletado >= totalP;

                return (
                    <Space>
                        {/* Admin Buttons */}
                        {isAdmin && (
                            <>
                                {!isUnlimited && (
                                    <Tooltip title="Editar Intentos (Admin)">
                                        <Button
                                            shape="circle"
                                            onClick={(e) => handleEditAttempts(record, e)}
                                            icon={<BiEdit />}
                                            style={{
                                                backgroundColor: "#1890ff",
                                                border: "none",
                                                color: "#fff",
                                                marginRight: 5
                                            }}
                                        />
                                    </Tooltip>
                                )}
                                <Tooltip title="Editar Píldora">
                                    <Button
                                        shape="circle"
                                        icon={<EditOutlined />}
                                        onClick={(e) => handleEditCaza(record, e)}
                                        style={{ marginRight: 5 }}
                                    />
                                </Tooltip>
                                <Tooltip title={record.id_bloqueo ? `Bloqueo activo: requiere "${record.bloqueo_nombre || ('Píldora #' + record.id_bloqueo)}"` : "Configurar bloqueo de acceso"}>
                                    <Button
                                        shape="circle"
                                        icon={<LockOutlined />}
                                        onClick={(e) => handleOpenBloqueoModal(record, e)}
                                        style={{
                                            backgroundColor: record.id_bloqueo ? '#fff7e6' : '#fafafa',
                                            borderColor: record.id_bloqueo ? '#ffa940' : '#d9d9d9',
                                            color: record.id_bloqueo ? '#fa8c16' : '#8c8c8c',
                                            marginRight: 5
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title="Configurar Recompensas">
                                    <Button
                                        shape="circle"
                                        icon={<TrophyOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedCazaRecompenses(record);
                                            setCazaRecompensesVisible(true);
                                        }}
                                        style={{ backgroundColor: "#fa8c16", border: "none", color: "#fff", marginRight: 5 }}
                                    />
                                </Tooltip>
                                <Tooltip title="Ver Estadísticas Alumnos">
                                    <Button
                                        shape="circle"
                                        icon={<UserOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveAdminCaza(record);
                                            setAdminModalVisible(true);
                                        }}
                                        style={{ backgroundColor: "#722ed1", border: "none", color: "#fff", marginRight: 5 }}
                                    />
                                </Tooltip>
                                <Tooltip title="Duplicar a otras aulas">
                                    <Button
                                        shape="circle"
                                        icon={<FaCopy />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setCopyCaza(record);
                                            setModalCopyVisible(true);
                                        }}
                                        style={{ backgroundColor: JutgeColors.colors.color2, border: "none", color: "#fff", marginRight: 5 }}
                                    />
                                </Tooltip>
                            </>
                        )}

                        {/* Claim Reward Button - only after ALL steps are completed */}
                        {allStepsDone && (!obtained || obtained === 0) && (
                            record.tiempo_limite_superado === 1 ? (
                                <Tooltip title={`Tiempo límite superado (${record.reward_time_limit} min). No puedes conseguir la recompensa.`}>
                                    <Button
                                        shape="circle"
                                        icon={<GiftOutlined />}
                                        disabled
                                        style={{
                                            backgroundColor: "#f5f5f5",
                                            color: "#d9d9d9",
                                            borderColor: "#d9d9d9",
                                            cursor: "not-allowed"
                                        }}
                                    />
                                </Tooltip>
                            ) : (
                                <Tooltip title="Reclamar Recompensa!">
                                    <Button
                                        shape="circle"
                                        icon={<GiftOutlined />}
                                        className="animate-bounce"
                                        style={{
                                            backgroundColor: "#f5222d",
                                            color: "#fff",
                                            border: "none",
                                            boxShadow: "0 2px 8px rgba(245, 34, 45, 0.4)"
                                        }}
                                        onClick={() => handleClaimAndRefresh(record)}
                                    />
                                </Tooltip>
                            )
                        )}

                        {/* Jugar Input Button (Restored Green Arrow Style) */}
                        {/* Visible for Admin ALWAYS (so they can test entering) OR for students if eligible */}
                        {(() => {
                            if (!isAdmin && (isFinalized || isCaptured || isEvaluating || allStepsDone)) {
                                return null;
                            }

                            const isStudent = !isAdmin;
                            const isGameMode = typeof window !== "undefined" && (window.location.search.includes("fromGame=true") || window.self !== window.top);
                            const isChallengeMode = (user as any)?.modoDesafio === 1 || (user as any)?.modoDesafio === true;
                            const isLocked = (isChallengeMode || isGameMode) && isStudent && record.id_bloqueo && !record.bloqueo_intentado;

                            if (isLocked) {
                                const reqName = record.bloqueo_nombre || "la píldora previa requerida";
                                return (
                                    <Tooltip title={`🔒 Bloqueado: debes realizar al menos un intento en "${reqName}" para desbloquear el acceso.`}>
                                        <Button
                                            disabled
                                            shape="circle"
                                            icon={<LockOutlined />}
                                            style={{ backgroundColor: "#f5f5f5", border: "1px solid #d9d9d9", color: "#bfbfbf", cursor: "not-allowed" }}
                                        />
                                    </Tooltip>
                                );
                            }

                            return (
                                <Tooltip title={isOutOfAttempts ? "Sin intentos" : "Entrar en la píldora"}>
                                    <Button
                                        shape="circle"
                                        icon={<BiArrowFromLeft />} // Restore BiArrowFromLeft
                                        disabled={!isAdmin && (estat === -1 || isOutOfAttempts)}
                                        style={{
                                            backgroundColor: (!isAdmin && isOutOfAttempts) ? "#ccc" : "#52c41a",
                                            border: "none",
                                            color: "#fff",
                                        }}
                                        onClick={() => showConfirm(record)}
                                    />
                                </Tooltip>
                            );
                        })()}
                    </Space>
                );
            },
        },
    ];

    const columns = isAdmin ? [
        {
            key: 'sort',
            align: 'center' as const,
            width: 30,
            render: () => <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />
        },
        ...baseColumns
    ] : baseColumns;

    const { confirm } = Modal;
    const showConfirm = (record: EscenarioCaza) => {
        const props: ModalFuncProps = {
            title: `🚀 ¡Preparado para el Aprendizaje Guiado?`,
            content: (
                <div style={{ lineHeight: "1.6em" }}>
                    <p>📚 <strong>Aprendizaje Paso a Paso:</strong> Irás resolviendo el ejercicio de forma incremental.</p>
                    <p>📖 <strong>Teoría Integrada:</strong> Tendrás acceso directo a la documentación de teoría de GitLab Pages en cualquier momento.</p>
                    <p>⚡ <strong>Validación por Tramos:</strong> Podrás validar cada paso conservando tu código para el siguiente.</p>
                    <p>⏱ Dispondrás de tiempo para completar la misión y ganar tu recompensa.</p>
                </div>
            ),
            okText: "¡Empezar!",
            cancelText: "Cancelar",
            onOk() {
                PostCazaStart(record.id.toString(), userId).then(() => {
                    setEscenarioActivo(record);
                }).catch(e => {
                    console.error("Error starting Caza:", e);
                    Modal.error({ title: "Error", content: "No se pudo iniciar la sesión." });
                });
            },
        };

        const isUnlimited = record.intentos_global === 0 || record.intentos_global === null || record.intentos_global === undefined;
        if (!isUnlimited && record.intentos_restantes !== undefined && record.intentos_restantes <= 0) {
            Modal.warning({ title: "Sin intentos", content: "No te quedan intentos para esta caza." });
            return;
        }
        // Forzamos que confirm reciba un ModalFuncProps
        (confirm as (props: ModalFuncProps) => void)(props);
    };

    return (
        <>
            <AppSider currentVal={5} setPantalla={() => { }} />

            <Content
                style={{
                    background: "#f8f9fb",
                    padding: 16,
                }}
            >
                <Card
                    bordered={false}
                    style={{
                        boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                        borderRadius: 12,
                        background: "#fff",
                    }}
                >
                    <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
                        <Typography.Text strong>Filtro global:</Typography.Text>
                        <Select
                            allowClear
                            placeholder="Todas las aulas"
                            style={{ minWidth: 200 }}
                            value={selectedAulaFilter}
                            onChange={setSelectedAulaFilter}
                            options={aulaSelectOptions}
                        />
                    </div>
                    <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
                        <SortableContext items={escenarios.map(i => i.id.toString())} strategy={verticalListSortingStrategy}>
                            <Table
                                components={{
                                    body: { row: Row },
                                }}
                                columns={columns}
                                dataSource={selectedAulaFilter && selectedAulaFilter !== 'all' ? escenarios.filter(e => e.id_aula === selectedAulaFilter) : escenarios}
                                showHeader={false}
                                rowKey={(record) => record.id.toString()}
                                pagination={{ pageSize: 8 }}
                                size="small"
                                bordered={false}
                                loading={loading}
                                style={{
                                    borderRadius: 12,
                                    overflow: "hidden",
                                    background: "#fff",
                                }}
                            />
                        </SortableContext>
                    </DndContext>
                </Card>
            </Content>

            {escenarioActivo && (
                <CazaModal
                    escenario={escenarioActivo}
                    isRetro={true}
                    onExit={() => {
                        setEscenarioActivo(null);
                        fetchData(); // Refresh on exit
                    }}
                />
            )}



            {selectedCaza && (
                <EditAttemptsModal
                    visible={editAttemptsVisible}
                    onClose={() => {
                        setEditAttemptsVisible(false);
                        fetchData();
                    }}
                    idCaza={selectedCaza.id}
                    nomCaza={selectedCaza.nombre}
                />
            )}

            {cazaBloqueoModalVisible && selectedCazaBloqueo && (
                <Modal
                    open={cazaBloqueoModalVisible}
                    title={
                        <Space>
                            <LockOutlined style={{ color: '#fa8c16' }} />
                            <span>Bloqueo de acceso (Prerrequisito) - {selectedCazaBloqueo.nombre}</span>
                        </Space>
                    }
                    onCancel={() => {
                        setCazaBloqueoModalVisible(false);
                        setSelectedCazaBloqueo(null);
                    }}
                    onOk={handleSaveCazaBloqueo}
                    confirmLoading={savingCazaBloqueo}
                    okText="Guardar"
                    cancelText="Cancelar"
                    centered
                >
                    <div style={{ padding: '8px 0' }}>
                        <p style={{ color: '#595959', fontSize: 13, marginBottom: 16 }}>
                            Configura qué píldora previa debe haber sido intentada <strong>al menos una vez</strong> por el alumno antes de poder acceder a esta píldora.
                            <br />
                            <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                                (Este bloqueo solo se aplica en <strong>Modo Desafío</strong> y <strong>Modo Juego</strong>; en el modo normal el acceso no se bloquea).
                            </span>
                        </p>

                        <div style={{ marginBottom: 8, fontWeight: 600 }}>Píldora previa requerida:</div>
                        <Select
                            allowClear
                            placeholder="Sin bloqueo (Acceso libre)"
                            style={{ width: '100%' }}
                            value={selectedCazaBloqueoId}
                            onChange={(val) => setSelectedCazaBloqueoId(val)}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option: any) => {
                                const text = Array.isArray(option?.children)
                                    ? option.children.join(' ')
                                    : String(option?.children ?? option?.label ?? '');
                                return text.toLowerCase().includes(input.toLowerCase());
                            }}
                        >
                            {(escenarios || [])
                                .filter((c: any) => {
                                    if (c.id === selectedCazaBloqueo.id) return false;
                                    // Misma aula si la píldora actual tiene aula asignada
                                    const targetAula = selectedCazaBloqueo.id_aula ?? null;
                                    const cAula = c.id_aula ?? null;
                                    if (targetAula !== null && cAula !== null) {
                                        return String(cAula) === String(targetAula);
                                    }
                                    return true;
                                })
                                .map((c: any) => (
                                    <Select.Option key={c.id} value={c.id}>
                                        🔒 {c.nombre} {c.aula ? `(${c.aula})` : ''}
                                    </Select.Option>
                                ))
                            }
                        </Select>
                    </div>
                </Modal>
            )}

            <NouCazaModal
                visible={newCazaVisible}
                onClose={() => {
                    setNewCazaVisible(false);
                    setEditingCaza(null);
                }}
                onSuccess={() => {
                    setNewCazaVisible(false);
                    setEditingCaza(null);
                    fetchData();
                }}
                caza={editingCaza}
                availableCazas={escenarios}
            />

            {selectedCazaRecompenses && (
                <PildoraRecompensesModal
                    visible={cazaRecompensesVisible}
                    onClose={(hasChanges) => {
                        setCazaRecompensesVisible(false);
                        setSelectedCazaRecompenses(null);
                        if (hasChanges) {
                            setRefreshKey(k => k + 1);
                            fetchData();
                        }
                    }}
                    caza={selectedCazaRecompenses}
                />
            )}

            {recompensaModalVisible && (
                <RecompensaSummaryModal
                    visible={recompensaModalVisible}
                    onClose={() => {
                        setRecompensaModalVisible(false);
                        setSelectableMode(false);
                        setSelectionOptions([]);
                        setPendingSelectionItem(null);
                        if (pendingJavamonBattle) {
                            setActiveBattleCazaId(pendingJavamonBattle.idCaza);
                            setBattleJavamon(pendingJavamonBattle.javamon);
                            setShowBattle(true);
                            setPendingJavamonBattle(null);
                        }
                    }}
                    rewards={recompensaItems}
                    selectableMode={selectableMode}
                    selectionOptions={selectionOptions}
                    onConfirmSelection={handleConfirmSelection}
                />
            )}

            {showBattle && (
                <JavaMon
                    userId={userId}
                    onClose={() => {
                        setShowBattle(false);
                        fetchData();
                    }}
                    onSuccess={() => {
                        if (activeBattleCazaId) {
                            ClaimCazaReward(activeBattleCazaId, userId).then(res => {
                                message.success("¡Javamon capturado y registrado con éxito!");
                                fetchData();
                            });
                        }
                    }}
                    forcedJavamon={battleJavamon}
                />
            )}

            <FloatButton.Group shape="circle" style={{ left: 24, bottom: 24 }}>
                <FloatButton icon={<SyncOutlined />} onClick={fetchData} tooltip="Refrescar" />
                {(user.tipus === "0" || user.tipus === "1") && (
                    <>
                        <FloatButton
                            icon={<FileExcelOutlined style={{ color: "#217346" }} />}
                            onClick={() => setImportExcelVisible(true)}
                            tooltip="Crear Píldora desde Excel"
                        />
                        <FloatButton
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setEditingCaza(null);
                                setNewCazaVisible(true);
                            }}
                            tooltip="Nueva Píldora"
                        />
                    </>
                )}
            </FloatButton.Group>

            {activeAdminCaza && (
                <CazaAdminModal
                    visible={adminModalVisible}
                    onClose={() => setAdminModalVisible(false)}
                    idCaza={activeAdminCaza.id.toString()}
                    cazaTitle={activeAdminCaza.nombre}
                    caza={activeAdminCaza}
                />
            )}

            {copyCaza && (
                <CopyPildoraModal
                    id_caza={copyCaza.id}
                    visible={modalCopyVisible}
                    onClose={(updated) => {
                        setModalCopyVisible(false);
                        setCopyCaza(null);
                        if (updated) fetchData();
                    }}
                />
            )}

            <ImportarCazaExcelModal
                visible={importExcelVisible}
                onClose={() => setImportExcelVisible(false)}
                onSuccess={fetchData}
            />
        </>
    );
};

export default Caza;
